import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, TextField,
  Divider, CircularProgress, Chip, IconButton, Alert,
  Collapse, LinearProgress, Checkbox, FormControlLabel, Tooltip
} from '@mui/material';

import SyncIcon from '@mui/icons-material/Sync';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TagIcon from '@mui/icons-material/Tag';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const getTodayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getEstimatedBlocks = (modo, fechaInicio, fechaFin) => {
  if (modo !== 'rango') return 1;

  const start = new Date(`${fechaInicio}T00:00:00`);
  const end = new Date(`${fechaFin}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return 1;

  const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(1, Math.ceil(days / 7));
};

const MODOS = [
  {
    key: 'hoy',
    label: 'Hoy',
    shortDesc: getTodayISO(),
    icon: CalendarTodayIcon,
    color: '#0655A5',
    bg: '#f0f7ff',
    border: '#d1e3f8',
  },
  {
    key: 'rango',
    label: 'Por Rango',
    shortDesc: 'Fechas personalizadas',
    icon: DateRangeIcon,
    color: '#7b1fa2',
    bg: '#f9f0ff',
    border: '#e1c8f5',
  },
  {
    key: 'folio',
    label: 'Por Folio',
    shortDesc: 'Número de OC',
    icon: TagIcon,
    color: '#2e7d32',
    bg: '#f0faf0',
    border: '#c8e6c9',
  },
];

export default function SyncDialog({
  onSync,
  defaultFechaInicio = getTodayISO(),
  defaultFechaFin = getTodayISO(),
  defaultFolio = '',
  size = 'small',
  isCompact = false,
  iconOnly = false,
  btnSx = {},
}) {
  const [open, setOpen] = useState(false);
  const [modo, setModo] = useState('hoy');
  const [fechaInicio, setFechaInicio] = useState(defaultFechaInicio);
  const [fechaFin, setFechaFin] = useState(defaultFechaFin);
  const [folioOC, setFolioOC] = useState(defaultFolio);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [warningsOpen, setWarningsOpen] = useState(false);

  const folioRef = useRef(null);

  // Auto-focus folio input when switching to folio mode
  useEffect(() => {
    if (modo === 'folio' && open) {
      setTimeout(() => folioRef.current?.focus(), 120);
    }
  }, [modo, open]);

  useEffect(() => {
    if (!loading) {
      setProgress(0);
      setElapsedSeconds(0);
      return undefined;
    }

    const startedAt = Date.now();
    setProgress(8);
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedSeconds(elapsed);
      setProgress((prev) => Math.min(92, prev + (prev < 55 ? 5 : 2)));
    }, 1000);

    return () => clearInterval(interval);
  }, [loading]);

  const handleOpen = () => {
    setResult(null);
    setError('');
    setWarningsOpen(false);
    setFolioOC(defaultFolio || '');
    setOpen(true);
  };

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
    setResult(null);
    setError('');
    setFolioOC(defaultFolio || '');
  };

  const validate = useCallback(() => {
    if (modo === 'rango') {
      if (!fechaInicio || !fechaFin) return 'Selecciona fecha de inicio y fecha final.';
      if (fechaInicio > fechaFin) return 'La fecha de inicio no puede ser mayor a la fecha final.';
    }
    if (modo === 'folio') {
      if (!folioOC.trim()) return 'Captura el número de folio.';
    }
    return '';
  }, [modo, fechaInicio, fechaFin, folioOC]);

  const handleSync = useCallback(async () => {
    const err = validate();
    if (err) { setError(err); return; }

    setError('');
    setLoading(true);
    setProgress(8);
    setElapsedSeconds(0);
    setResult(null);

    try {
      const fi = modo === 'rango' ? fechaInicio : getTodayISO();
      const ff = modo === 'rango' ? fechaFin : getTodayISO();
      const folio = modo === 'folio' ? folioOC.trim() : null;

      const resp = await onSync(modo, { fechaInicio: fi, fechaFin: ff, folioOC: folio });

      if (resp?.ok) {
        setResult({
          ok: true,
          mensaje: resp.mensaje || 'Sincronización completada correctamente.',
          resumen: resp.resumen,
          warnings: resp.warnings || [],
        });
      } else {
        setResult({
          ok: false,
          sinResultados: resp?.sinResultados ?? true,
          mensaje: resp?.mensaje || 'El ERP no encontró registros para ese criterio.',
          warnings: resp?.warnings || [],
        });
      }
    } catch (e) {
      setResult({
        ok: false,
        sinResultados: false,
        mensaje: e?.message || 'Error de conexión al sincronizar con ERP.',
        warnings: [],
      });
    } finally {
      setLoading(false);
    }
  }, [modo, fechaInicio, fechaFin, folioOC, onSync, validate]);

  const activeModo = MODOS.find((m) => m.key === modo);
  const estimatedBlocks = getEstimatedBlocks(modo, fechaInicio, fechaFin);
  const progressLabel = (() => {
    if (!loading) return '';
    if (progress < 28) return 'Preparando consulta al ERP';
    if (progress < 58) return modo === 'rango'
      ? `Consultando ERP por bloques (${estimatedBlocks} estimado${estimatedBlocks > 1 ? 's' : ''})`
      : 'Consultando ERP';
    if (progress < 82) return 'Guardando ordenes, partidas y documentos';
    return 'Actualizando resultado en pantalla';
  })();

  return (
    <>
      {/* ── Trigger button ── */}
      {iconOnly ? (
        <Tooltip title="Sincronizar ERP">
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpen}
            sx={{
              minWidth: 34,
              width: 34,
              height: 34,
              p: 0,
              borderRadius: '10px',
              bgcolor: '#ffffff',
              borderColor: '#cbd5e1',
              color: '#0655A5',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#f0f7ff', borderColor: '#0655A5' },
              ...btnSx,
            }}
          >
            <SyncIcon sx={{ fontSize: 18 }} />
          </Button>
        </Tooltip>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SyncIcon sx={{ fontSize: isCompact ? '13px !important' : 15 }} />}
          endIcon={<ArrowDropDownIcon sx={{ fontSize: isCompact ? '15px !important' : 17 }} />}
          onClick={handleOpen}
          sx={{
            height: isCompact ? 26 : 32,
            fontSize: isCompact ? '0.68rem' : '0.72rem',
            textTransform: 'none',
            fontWeight: 700,
            color: '#0655A5',
            borderColor: '#d1e3f8',
            borderRadius: isCompact ? '10px' : 2,
            px: isCompact ? 1 : 1.5,
            bgcolor: 'rgba(6,85,165,0.03)',
            '&:hover': { borderColor: '#0655A5', bgcolor: 'rgba(6,85,165,0.07)' },
            ...btnSx,
          }}
        >
          Sincronizar
        </Button>
      )}

      {/* ── Dialog ── */}
      <Dialog
        open={open}
        disableRestoreFocus
        onClose={(event, reason) => {
          if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          handleClose();
        }}
        disableEscapeKeyDown={loading}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'clamp(280px, 32vw, 420px)',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            m: 1.5,
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: '#0655A5', color: '#fff', py: 1.2, px: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon sx={{ fontSize: 16 }} />
            <Typography fontWeight={800} fontSize="0.84rem">Sincronizar con ERP</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={loading}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' }, p: 0.3 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* ── Mode cards ── */}
          <Box sx={{ display: 'flex', gap: 0.8, px: 1.5, pt: 1.5, pb: 1 }}>
            {MODOS.map((m) => {
              const Icon = m.icon;
              const selected = modo === m.key;
              return (
                <Box
                  key={m.key}
                  onClick={() => { setModo(m.key); setError(''); setResult(null); }}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    py: 0.9,
                    px: 0.4,
                    borderRadius: '10px',
                    border: `1.5px solid ${selected ? m.color : '#e5e7eb'}`,
                    bgcolor: selected ? m.bg : '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    userSelect: 'none',
                    '&:hover': { bgcolor: m.bg, borderColor: m.color + '80' },
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: selected ? m.color : '#b0b8c4' }} />
                  <Typography fontSize="0.7rem" fontWeight={selected ? 800 : 600} color={selected ? m.color : 'text.secondary'}>
                    {m.label}
                  </Typography>
                  <Typography fontSize="0.6rem" color={selected ? m.color + 'cc' : 'text.disabled'} sx={{ lineHeight: 1.1, textAlign: 'center' }}>
                    {m.shortDesc}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Divider sx={{ borderColor: '#f1f5f9', mx: 1.5 }} />

          {/* ── Dynamic form ── */}
          <Box sx={{ px: 1.5, pt: 1.2, pb: 0.8 }}>
            {modo === 'hoy' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 1.2, bgcolor: '#f0f7ff', borderRadius: '10px', border: '1px solid #d1e3f8' }}>
                <CalendarTodayIcon sx={{ fontSize: 18, color: '#0655A5', flexShrink: 0 }} />
                <Box>
                  <Typography fontSize="0.78rem" fontWeight={800} color="#0655A5">
                    {getTodayISO()}
                  </Typography>
                  <Typography fontSize="0.68rem" color="text.secondary">
                    Importa todas las OC cuya fecha sea hoy.
                  </Typography>
                </Box>
              </Box>
            )}

            {modo === 'rango' && (
              <Box sx={{ display: 'flex', gap: 1.25 }}>
                <TextField
                  label="Inicio"
                  type="date"
                  size="small"
                  fullWidth
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); setError(''); setResult(null); }}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
                <TextField
                  label="Final"
                  type="date"
                  size="small"
                  fullWidth
                  value={fechaFin}
                  onChange={(e) => { setFechaFin(e.target.value); setError(''); setResult(null); }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: fechaInicio }}
                  sx={fieldSx}
                />
              </Box>
            )}

            {modo === 'folio' && (
              <TextField
                inputRef={folioRef}
                label="Folio de la OC"
                placeholder="Ej: 44744"
                size="small"
                fullWidth
                value={folioOC}
                onChange={(e) => { setFolioOC(e.target.value); setError(''); setResult(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSync(); }}
                sx={fieldSx}
                InputProps={{
                  startAdornment: <TagIcon sx={{ fontSize: 16, color: '#2e7d32', mr: 0.75, flexShrink: 0 }} />,
                }}
              />
            )}

            {/* Validation error */}
            {error && (
              <Alert severity="warning" sx={{ mt: 1.25, py: 0.4, fontSize: '0.73rem', borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          {/* ── Result block ── */}
          {loading && (
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #d1e3f8',
                bgcolor: '#f8fbff',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography fontSize="0.78rem" fontWeight={800} color="#0655A5">
                    {progressLabel}
                  </Typography>
                  <Typography fontSize="0.68rem" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {elapsedSeconds}s
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    bgcolor: '#e5eef8',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      backgroundColor: activeModo?.color || '#0655A5',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  <Chip
                    size="small"
                    label={modo === 'folio' ? `Folio ${folioOC.trim()}` : modo === 'hoy' ? 'Hoy' : `${fechaInicio} a ${fechaFin}`}
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#eef6ff', color: '#0655A5' }}
                  />
                  {modo === 'rango' && (
                    <Chip
                      size="small"
                      label={`${estimatedBlocks} bloque${estimatedBlocks > 1 ? 's' : ''}`}
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f3e8ff', color: '#6b21a8' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {result && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Alert
                severity={result.ok ? 'success' : result.sinResultados ? 'info' : 'error'}
                icon={
                  result.ok
                    ? <CheckCircleOutlineIcon fontSize="small" />
                    : result.sinResultados
                    ? <InfoOutlinedIcon fontSize="small" />
                    : undefined
                }
                sx={{ py: 0.75, borderRadius: 2 }}
              >
                <Typography fontSize="0.8rem" fontWeight={700}>{result.mensaje}</Typography>

                {/* Stats chips - Entradas, Pagos y Partidas */}
                {result.ok && result.resumen && (() => {
                  const { 
                    nuevasOCs, 
                    ocActualizadas, 
                    partidasInsertadas, 
                    partidasActualizadas, 
                    entradasNuevas = 0,
                    entradasActualizadas = 0,
                    entradasSincronizadas = 0, 
                    entradasPartidasSincronizadas, 
                    pagosNuevos = 0,
                    pagosActualizados = 0,
                    pagosErpSincronizados = 0 
                  } = result.resumen;

                  const chips = [
                    // Entradas desglosadas
                    entradasNuevas > 0 && { label: `${entradasNuevas} Entradas nuevas`, color: '#0369a1', bg: '#e0f2fe' },
                    entradasActualizadas > 0 && { label: `${entradasActualizadas} Entradas verificadas/actualizadas`, color: '#0284c7', bg: '#f0f9ff' },
                    (entradasNuevas === 0 && entradasActualizadas === 0 && entradasSincronizadas > 0) && { label: `${entradasSincronizadas} Entradas sincronizadas`, color: '#0369a1', bg: '#e0f2fe' },
                    entradasPartidasSincronizadas > 0 && { label: `${entradasPartidasSincronizadas} Partidas de Entradas`, color: '#0284c7', bg: '#f0f9ff' },
                    
                    // Pagos desglosados
                    pagosNuevos > 0 && { label: `${pagosNuevos} Pagos nuevos`, color: '#15803d', bg: '#dcfce7' },
                    pagosActualizados > 0 && { label: `${pagosActualizados} Pagos verificados/sin cambios`, color: '#166534', bg: '#f0fdf4' },
                    (pagosNuevos === 0 && pagosActualizados === 0 && pagosErpSincronizados > 0) && { label: `${pagosErpSincronizados} Pagos ERP sincronizados`, color: '#15803d', bg: '#dcfce7' },

                    partidasInsertadas > 0 && { label: `${partidasInsertadas} Partidas nuevas`, color: '#6b21a8', bg: '#f3e8ff' },
                    partidasActualizadas > 0 && { label: `${partidasActualizadas} Partidas actualizadas`, color: '#a21caf', bg: '#fae8ff' },
                    nuevasOCs > 0 && { label: `${nuevasOCs} OC nuevas`, color: '#1b5e20', bg: '#e8f5e9' },
                    ocActualizadas > 0 && { label: `${ocActualizadas} OC actualizadas`, color: '#b45309', bg: '#fef3c7' },
                  ].filter(Boolean);

                  return chips.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      {chips.map(({ label, color, bg }) => (
                        <Chip key={label} label={label} size="small"
                          sx={{ fontSize: '0.68rem', height: 21, bgcolor: bg, color, fontWeight: 700, border: 'none' }}
                        />
                      ))}
                    </Box>
                  ) : null;
                })()}
              </Alert>

              {/* Warnings collapsible */}
              {result.warnings?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Box
                    onClick={() => setWarningsOpen((v) => !v)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      cursor: 'pointer', px: 1.25, py: 0.75,
                      bgcolor: '#fffbeb', borderRadius: 1.5,
                      border: '1px solid #fde68a',
                      '&:hover': { bgcolor: '#fef3c7' },
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 15, color: '#d97706' }} />
                    <Typography fontSize="0.72rem" fontWeight={700} color="#92400e" flex={1}>
                      {result.warnings.length} aviso{result.warnings.length > 1 ? 's' : ''} del proceso
                    </Typography>
                    <ExpandMoreIcon sx={{
                      fontSize: 16, color: '#d97706',
                      transform: warningsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }} />
                  </Box>
                  <Collapse in={warningsOpen}>
                    <Box sx={{
                      mt: 0.5, pl: 1.5, pr: 1, py: 0.75,
                      bgcolor: '#fffbeb', borderRadius: 1.5,
                      border: '1px solid #fde68a',
                      maxHeight: 140,
                      overflowY: 'auto',
                    }}>
                      {result.warnings.map((w, i) => (
                        <Typography key={i} fontSize="0.68rem" color="#92400e" sx={{ mb: 0.4, lineHeight: 1.5 }}>
                          • {w}
                        </Typography>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {/* ── Footer ── */}
        <DialogActions sx={{ px: 1.5, pb: 1.5, pt: 0.5, gap: 0.8 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              borderRadius: '8px', height: 32, textTransform: 'none',
              fontWeight: 600, color: 'text.secondary', fontSize: '0.72rem',
              flex: 1,
            }}
          >
            {result?.ok ? 'Cerrar' : 'Cancelar'}
          </Button>

          <Button
            variant="contained"
            onClick={handleSync}
            disabled={loading}
            startIcon={
              loading
                ? <CircularProgress size={13} color="inherit" />
                : <SyncIcon sx={{ fontSize: 15 }} />
            }
            sx={{
              borderRadius: '8px', height: 32, textTransform: 'none',
              fontWeight: 800, fontSize: '0.74rem', flex: 2,
              background: `linear-gradient(135deg, ${activeModo?.color || '#0655A5'} 0%, ${activeModo?.color || '#054785'}cc 100%)`,
              boxShadow: `0 4px 14px ${activeModo?.color || '#0655A5'}40`,
              '&:hover': { boxShadow: `0 6px 18px ${activeModo?.color || '#0655A5'}60` },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
          >
            {loading
              ? 'Sincronizando...'
              : result?.ok
              ? `Sincronizar ${activeModo?.label || ''} de nuevo`
              : result?.sinResultados
              ? 'Reintentar'
              : `Sincronizar ${activeModo?.label || ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    fontSize: '0.82rem',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#0655A5' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.78rem', fontWeight: 600 },
};
